import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { settingsService } from '../services/api';
import './Settings.css';

const SETTING_META = {
  'premium.night-shift-rate': {
    label: 'Nachtzuschlag',
    type: 'number',
    tooltip: 'Multiplikator für Nachtarbeit. 0.25 entspricht 25 % des Stundenlohns.',
  },
  'premium.sunday-rate': {
    label: 'Sonntagszuschlag',
    type: 'number',
    tooltip: 'Multiplikator für Sonntagsarbeit. Gilt für Arbeit an Sonn- und Feiertagen (nicht Feiertage).',
  },
  'premium.holiday-rate': {
    label: 'Feiertagszuschlag',
    type: 'number',
    tooltip: 'Multiplikator für gesetzliche Feiertage. Bei TVöD typischerweise 1.35 (= 135 % des Stundenlohns).',
  },
  'premium.christmas-newyear-rate': {
    label: 'Weihnachten / Neujahr',
    type: 'number',
    tooltip: 'Erhöhter Zuschlag für Heiligabend ab 14 Uhr, 25./26.12. und Neujahr (1.1.). TVöD: 0.35.',
  },
  'premium.saturday-rate': {
    label: 'Samstagszuschlag',
    type: 'number',
    tooltip: 'Multiplikator für Samstagsarbeit innerhalb der festgelegten Samstagsstunden.',
  },
  'night-shift.start': {
    label: 'Beginn (Stunde)',
    type: 'integer',
    tooltip: 'Stunde (0–23), ab der die Nachtschicht beginnt. Standard: 21 Uhr.',
  },
  'night-shift.end': {
    label: 'Ende (Stunde)',
    type: 'integer',
    tooltip: 'Stunde (0–23), bis zu der die Nachtschicht gilt. Standard: 6 Uhr.',
  },
  'saturday-shift.start': {
    label: 'Beginn (Stunde)',
    type: 'integer',
    tooltip: 'Ab welcher Stunde am Samstag der Samstagszuschlag gilt. Standard: 13 Uhr.',
  },
  'saturday-shift.end': {
    label: 'Ende (Stunde)',
    type: 'integer',
    tooltip: 'Bis zu welcher Stunde am Samstag der Samstagszuschlag gilt. Standard: 21 Uhr.',
  },
  'project.premium-time-id': {
    label: 'Projekt-ID Zeitzuschläge',
    type: 'integer',
    tooltip: 'Kimai-Projekt-ID, unter der alle Zuschlagsbuchungen angelegt werden.',
  },
  'activity.holiday-work-id': {
    label: 'Aktivität: Feiertagsarbeit',
    type: 'integer',
    tooltip: 'Kimai-Aktivitäts-ID für Feiertagsarbeit-Zuschläge.',
  },
  'activity.night-shift-id': {
    label: 'Aktivität: Nachtschicht',
    type: 'integer',
    tooltip: 'Kimai-Aktivitäts-ID für Nachtzuschläge.',
  },
  'activity.saturday-work-id': {
    label: 'Aktivität: Samstagsarbeit',
    type: 'integer',
    tooltip: 'Kimai-Aktivitäts-ID für Samstagszuschläge.',
  },
  'activity.sunday-work-id': {
    label: 'Aktivität: Sonntagsarbeit',
    type: 'integer',
    tooltip: 'Kimai-Aktivitäts-ID für Sonntagszuschläge.',
  },
  'activity.christmas-newyear-id': {
    label: 'Aktivität: Weihnachten / Neujahr',
    type: 'integer',
    tooltip: 'Kimai-Aktivitäts-ID für Weihnachten/Neujahr-Zuschläge.',
  },
  'processing.previous-days-to-check': {
    label: 'Tage rückwirkend prüfen',
    type: 'integer',
    tooltip: 'Anzahl der Tage, die bei der automatischen Verarbeitung rückwirkend auf neue Zeiteinträge geprüft werden.',
  },
  'processing.min-nightshift-duration': {
    label: 'Min. Nachtschichtdauer (Min.)',
    type: 'integer',
    tooltip: 'Minimale Dauer in Minuten, die ein Zeitraum innerhalb der Nachtzeit haben muss, um als Nachtschicht gezählt zu werden. 0 = keine Mindestdauer.',
  },
  'notification.send-mail-to-admin': {
    label: 'Zeitzuschläge an Admins melden',
    type: 'boolean',
    testType: 'premium-entries',
    tooltip: 'Prüft täglich um 01:00, ob erkannte Zeitzuschläge noch nicht in Kimai gebucht sind, und meldet diese an die Admin-Adressen.',
  },
  'notification.recipients-management': {
    label: 'Geschäftsführung',
    type: 'text',
    tooltip: 'E-Mail-Adressen der Geschäftsführung. Mehrere Adressen mit Komma trennen.',
  },
  'notification.recipients-works-council': {
    label: 'Betriebsrat',
    type: 'text',
    tooltip: 'E-Mail-Adressen des Betriebsrats. Mehrere Adressen mit Komma trennen.',
  },
  'notification.recipients-hr': {
    label: 'Personalabteilung',
    type: 'text',
    tooltip: 'E-Mail-Adressen der Personalabteilung. Mehrere Adressen mit Komma trennen.',
  },
  'notification.recipients-admin': {
    label: 'Administratoren',
    type: 'text',
    tooltip: 'E-Mail-Adressen der Administratoren – Empfänger der Zeitzuschlag-Meldungen. Mehrere Adressen mit Komma trennen.',
  },
  'notification.debug-mail': {
    label: 'Debug-Mail (Testversand)',
    type: 'text',
    placeholder: 'name@beispiel.de',
    tooltip: 'Test-Mails gehen ausschließlich an diese Adresse. Ohne Eintrag ist der Testversand gesperrt, damit echte Empfänger keine Testmails erhalten.',
  },
  'notification.notify-red-traffic-light': {
    label: 'Rote Ampel melden',
    type: 'boolean',
    testType: 'red-traffic-light',
    tooltip: 'Benachrichtigung, wenn ein*e Mitarbeiter*in den zweiten Monat in Folge außerhalb der Zeitkonto-Grenzen liegt (rote Ampel).',
  },
  'notification.notify-vacation-november': {
    label: 'Resturlaub im November melden',
    type: 'boolean',
    testType: 'vacation-november',
    tooltip: 'Benachrichtigung beim Snapshot am 1. November, wenn Mitarbeiter*innen noch zu viel Urlaub offen haben.',
  },
  'notification.notify-sick-days': {
    label: 'Viele Krankheitstage melden',
    type: 'boolean',
    testType: 'sick-days',
    tooltip: 'Benachrichtigung, wenn ein*e Mitarbeiter*in in den letzten 12 Monaten die unten eingestellte Zahl an Krankheitstagen erreicht.',
  },
  'notification.notify-monthly-report': {
    label: 'Monatlichen Übersichtsbericht senden',
    type: 'boolean',
    testType: 'monthly-report',
    tooltip: 'Verschickt nach jedem Snapshot einen Bericht über alle Mitarbeiter*innen mit Zeitkonto- und Urlaubstagen sowie einer Gesamtübersicht.',
  },
  'notification.notify-employee-personal': {
    label: 'Mitarbeitende persönlich benachrichtigen',
    type: 'boolean',
    testType: 'employee-personal',
    tooltip: '30 Minuten nach dem Monats-Snapshot erhalten Mitarbeitende außerhalb der grünen Ampel eine persönliche Mail an ihre in Kimai hinterlegte Adresse.',
  },
  'notification.vacation-november-percent': {
    label: 'Schwelle Resturlaub (%)',
    type: 'number',
    tooltip: 'Ab wie viel Prozent noch offenem Jahresurlaub im November gewarnt wird. Standard: 50 %.',
  },
  'notification.sick-days-threshold': {
    label: 'Schwelle Krankheitstage',
    type: 'number',
    tooltip: 'Ab wie vielen Krankheitstagen in den letzten 12 Monaten gewarnt wird. Standard: 30 Tage.',
  },
  'limits.lower-threshold-percent': {
    label: 'Untergrenze: Schwelle (%)',
    type: 'number',
    tooltip: 'Wochenarbeitszeit in % von 39 h, ab der die höhere Untergrenze gilt. Über dieser Schwelle greift „Untergrenze darüber", sonst „Untergrenze darunter". Standard: 65 %.',
  },
  'limits.lower-limit-below': {
    label: 'Untergrenze darunter (h)',
    type: 'number',
    tooltip: 'Untere Zeitkonto-Grenze (Stunden, negativ) bei Wochenarbeitszeit ≤ Schwelle. Standard: −7,5 h.',
  },
  'limits.lower-limit-above': {
    label: 'Untergrenze darüber (h)',
    type: 'number',
    tooltip: 'Untere Zeitkonto-Grenze (Stunden, negativ) bei Wochenarbeitszeit > Schwelle. Standard: −15 h.',
  },
  'limits.upper-threshold1-percent': {
    label: 'Obergrenze: Schwelle 1 (%)',
    type: 'number',
    tooltip: 'Untere Schwelle für die Obergrenze. Unter diesem Wert gilt „Obergrenze niedrig". Standard: 50 %.',
  },
  'limits.upper-threshold2-percent': {
    label: 'Obergrenze: Schwelle 2 (%)',
    type: 'number',
    tooltip: 'Obere Schwelle für die Obergrenze. Über diesem Wert gilt „Obergrenze hoch", dazwischen „Obergrenze mittel". Standard: 75 %.',
  },
  'limits.upper-limit-low': {
    label: 'Obergrenze niedrig (h)',
    type: 'number',
    tooltip: 'Obere Zeitkonto-Grenze (Stunden) bei Wochenarbeitszeit < Schwelle 1. Standard: +20 h.',
  },
  'limits.upper-limit-mid': {
    label: 'Obergrenze mittel (h)',
    type: 'number',
    tooltip: 'Obere Zeitkonto-Grenze (Stunden) bei Wochenarbeitszeit zwischen Schwelle 1 und 2. Standard: +30 h.',
  },
  'limits.upper-limit-high': {
    label: 'Obergrenze hoch (h)',
    type: 'number',
    tooltip: 'Obere Zeitkonto-Grenze (Stunden) bei Wochenarbeitszeit > Schwelle 2. Standard: +40 h.',
  },
};

const SECTIONS = [
  {
    title: 'Zeitzuschläge (Raten)',
    keys: [
      'premium.night-shift-rate',
      'premium.sunday-rate',
      'premium.holiday-rate',
      'premium.christmas-newyear-rate',
      'premium.saturday-rate',
    ],
  },
  {
    title: 'Nachtschicht-Zeiten',
    keys: ['night-shift.start', 'night-shift.end'],
  },
  {
    title: 'Samstagszuschlag-Zeiten',
    keys: ['saturday-shift.start', 'saturday-shift.end'],
  },
  {
    title: 'Kimai-Verknüpfung',
    keys: [
      'project.premium-time-id',
      'activity.holiday-work-id',
      'activity.night-shift-id',
      'activity.saturday-work-id',
      'activity.sunday-work-id',
      'activity.christmas-newyear-id',
    ],
  },
  {
    title: 'Verarbeitung',
    keys: [
      'processing.previous-days-to-check',
      'processing.min-nightshift-duration',
    ],
  },
  {
    title: 'Benachrichtigungen – Empfänger',
    keys: [
      'notification.recipients-management',
      'notification.recipients-works-council',
      'notification.recipients-hr',
      'notification.recipients-admin',
      'notification.debug-mail',
    ],
  },
  {
    title: 'Benachrichtigungen – Auslöser',
    keys: [
      'notification.notify-red-traffic-light',
      'notification.notify-vacation-november',
      'notification.notify-sick-days',
      'notification.notify-monthly-report',
      'notification.notify-employee-personal',
      'notification.send-mail-to-admin',
      'notification.vacation-november-percent',
      'notification.sick-days-threshold',
    ],
  },
  {
    title: 'Zeitkonto-Grenzen (Ampel)',
    keys: [
      'limits.lower-threshold-percent',
      'limits.lower-limit-below',
      'limits.lower-limit-above',
      'limits.upper-threshold1-percent',
      'limits.upper-threshold2-percent',
      'limits.upper-limit-low',
      'limits.upper-limit-mid',
      'limits.upper-limit-high',
    ],
  },
];

function InfoBtn({ tooltip }) {
  return (
    <span className="info-btn" data-tooltip={tooltip} aria-label="Info">?</span>
  );
}

function SettingField({ settingKey, value, onChange, defaults, onTest, testingType }) {
  const meta = SETTING_META[settingKey];
  if (!meta) return null;

  const defaultVal = defaults[settingKey];
  const isChanged  = value !== undefined && value !== defaultVal;

  if (meta.type === 'boolean') {
    return (
      <div className="setting-row">
        <div className="setting-label-wrap">
          <span className="setting-label">{meta.label}</span>
          <InfoBtn tooltip={meta.tooltip} />
        </div>
        <div className="setting-control">
          <label className="toggle-wrap">
            <input
              type="checkbox"
              className="toggle-input"
              checked={value === 'true'}
              onChange={e => onChange(settingKey, String(e.target.checked))}
            />
            <span className="toggle-slider" />
          </label>
          {meta.testType && onTest && (
            <button
              type="button"
              className="settings-toggle-test-btn"
              onClick={() => onTest(meta.testType)}
              disabled={testingType != null}
              title="Verschickt eine Test-Mail dieses Typs an die Debug-Adresse."
            >
              {testingType === meta.testType ? 'Sende…' : 'Test'}
            </button>
          )}
          {isChanged && <span className="setting-default">Standard: {defaultVal}</span>}
        </div>
      </div>
    );
  }

  if (meta.type === 'text') {
    return (
      <div className="setting-row">
        <div className="setting-label-wrap">
          <span className="setting-label">{meta.label}</span>
          <InfoBtn tooltip={meta.tooltip} />
        </div>
        <div className="setting-control">
          <input
            className="setting-input setting-input--text"
            type="text"
            placeholder={meta.placeholder ?? 'name@beispiel.de, name2@beispiel.de'}
            value={value ?? ''}
            onChange={e => onChange(settingKey, e.target.value)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="setting-row">
      <div className="setting-label-wrap">
        <span className="setting-label">{meta.label}</span>
        <InfoBtn tooltip={meta.tooltip} />
      </div>
      <div className="setting-control">
        <input
          className="setting-input"
          type="number"
          step={meta.type === 'number' ? '0.01' : '1'}
          value={value ?? ''}
          onChange={e => onChange(settingKey, e.target.value)}
        />
        {isChanged && <span className="setting-default">Standard: {defaultVal}</span>}
      </div>
    </div>
  );
}

export default function Settings() {
  const [profiles, setProfiles]             = useState([]);
  const [activeId, setActiveId]             = useState(null);
  const [settings, setSettings]             = useState({});
  const [defaults, setDefaults]             = useState({});
  const [loading, setLoading]               = useState(true);
  const [saving, setSaving]                 = useState(false);
  const [toast, setToast]                   = useState('');
  const [testingType, setTestingType]       = useState(null);
  const [newProfileName, setNewProfileName] = useState('');
  const [showNewProfile, setShowNewProfile] = useState(false);

  useEffect(() => {
    Promise.all([
      settingsService.getProfiles(),
      settingsService.getDefaults(),
    ]).then(([profilesRes, defaultsRes]) => {
      const profs = profilesRes.data;
      const defs  = defaultsRes.data;
      setProfiles(profs);
      setDefaults(defs);
      const initialProf = profs.find(p => p.active) || profs.find(p => p.defaultProfile) || profs[0];
      if (initialProf) setActiveId(initialProf.id);
    }).catch(() => showToast('Fehler beim Laden der Einstellungen.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeId == null) return;
    settingsService.getSettings(activeId)
      .then(res => setSettings(res.data))
      .catch(() => showToast('Fehler beim Laden des Profils.'));
  }, [activeId]);

  function handleChange(key, val) {
    setSettings(s => ({ ...s, [key]: val }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await settingsService.saveSettings(activeId, settings);
      showToast('Einstellungen gespeichert.');
    } catch {
      showToast('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  }

  async function handleTestMail(type) {
    setTestingType(type);
    try {
      const res = await settingsService.sendTestMail(type);
      const { recipients } = res.data;
      showToast(`Test-Mail an ${recipients?.join(', ') || 'die Debug-Adresse'} versendet.`);
    } catch (err) {
      showToast(err.response?.data?.message || 'Testversand fehlgeschlagen.');
    } finally {
      setTestingType(null);
    }
  }

  async function handleCreateProfile() {
    if (!newProfileName.trim()) return;
    try {
      const res = await settingsService.createProfile(newProfileName.trim());
      const newProf = res.data;
      setProfiles(p => [...p, newProf]);
      setActiveId(newProf.id);
      setNewProfileName('');
      setShowNewProfile(false);
    } catch {
      showToast('Fehler beim Erstellen des Profils.');
    }
  }

  async function handleActivate() {
    const prof = profiles.find(p => p.id === activeId);
    if (!prof || prof.active) return;
    try {
      await settingsService.activateProfile(activeId);
      setProfiles(ps => ps.map(p => ({ ...p, active: p.id === activeId })));
      showToast(`Profil "${prof.name}" aktiviert.`);
    } catch {
      showToast('Fehler beim Aktivieren des Profils.');
    }
  }

  async function handleDeleteProfile() {
    const prof = profiles.find(p => p.id === activeId);
    if (!prof || prof.defaultProfile) return;
    if (!window.confirm(`Profil "${prof.name}" wirklich löschen?`)) return;
    try {
      await settingsService.deleteProfile(activeId);
      // Deleting the active profile falls back to the default profile (backend).
      const fallbackActive = prof.active;
      const remaining = profiles
        .filter(p => p.id !== activeId)
        .map(p => fallbackActive && p.defaultProfile ? { ...p, active: true } : p);
      setProfiles(remaining);
      const next = remaining.find(p => p.defaultProfile) || remaining[0];
      setActiveId(next?.id ?? null);
    } catch {
      showToast('Fehler beim Löschen des Profils.');
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  const activeProfile = profiles.find(p => p.id === activeId);

  if (loading) {
    return (
      <div className="dash-root">
        <Sidebar />
        <main className="dash-main">
          <div className="dash-state">
            <div className="dash-spinner" />
            <span>Lade Einstellungen…</span>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dash-root">
      <Sidebar />
      <main className="dash-main">
        <header className="dash-header">
          <div>
            <h1 className="dash-title">Einstellungen</h1>
            <p className="dash-date">Systemkonfiguration für kimai-assist</p>
          </div>
        </header>

        <div className="settings-profile-bar">
          <span className="settings-label-sm">Profil:</span>
          <div className="profile-chips">
            {profiles.map(p => (
              <button
                key={p.id}
                className={`profile-chip${p.id === activeId ? ' profile-chip--selected' : ''}${p.active ? ' profile-chip--active' : ''}`}
                onClick={() => setActiveId(p.id)}
              >
                {p.active && <span className="profile-active-dot" aria-hidden="true" />}
                {p.name}
                {p.defaultProfile && <span className="profile-default-badge">Standard</span>}
                {p.active && <span className="profile-active-badge">Aktiv</span>}
              </button>
            ))}

            {showNewProfile ? (
              <div className="new-profile-form">
                <input
                  className="new-profile-input"
                  placeholder="Profilname…"
                  value={newProfileName}
                  onChange={e => setNewProfileName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleCreateProfile();
                    if (e.key === 'Escape') { setShowNewProfile(false); setNewProfileName(''); }
                  }}
                  autoFocus
                />
                <button className="profile-create-btn" onClick={handleCreateProfile}>Erstellen</button>
                <button className="profile-cancel-btn" onClick={() => { setShowNewProfile(false); setNewProfileName(''); }}>✕</button>
              </div>
            ) : (
              <button className="profile-add-btn" onClick={() => setShowNewProfile(true)}>+ Neues Profil</button>
            )}
          </div>
        </div>

        <div className="settings-card">
          {SECTIONS.map(section => (
            <div key={section.title} className="settings-section">
              <h2 className="section-title">{section.title}</h2>
              <div className="settings-grid">
                {section.keys.map(key => (
                  <SettingField
                    key={key}
                    settingKey={key}
                    value={settings[key] ?? defaults[key] ?? ''}
                    onChange={handleChange}
                    defaults={defaults}
                    onTest={handleTestMail}
                    testingType={testingType}
                  />
                ))}
              </div>
            </div>
          ))}

          <div className="settings-footer">
            <button
              className="settings-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Wird gespeichert…' : 'Einstellungen speichern'}
            </button>
            {activeProfile && (
              activeProfile.active ? (
                <span className="settings-active-hint">
                  <span className="profile-active-dot" aria-hidden="true" />
                  Aktives Profil
                </span>
              ) : (
                <button className="settings-activate-btn" onClick={handleActivate}>
                  Profil aktivieren
                </button>
              )
            )}
            {activeProfile && !activeProfile.defaultProfile && (
              <button className="settings-delete-btn" onClick={handleDeleteProfile}>
                Profil löschen
              </button>
            )}
          </div>
        </div>

        {toast && <div className="settings-toast">{toast}</div>}
      </main>
    </div>
  );
}
